/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { linkedAccountApi } from 'api';
import { dayjs } from 'utils/dayjs';
import { DialogAction, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Session } from 'types/Session';
import { usePanel } from 'contexts/panelContext';
import { SessionPayload } from 'types/payloads/SessionPayload';
import Calendar from 'components/_common/forms/Calendar';
import TextArea from 'components/_common/forms/TextArea';
import { useGlobal } from 'contexts/globalContext';
import ValidateCheckbox from 'components/_common/forms/Checkbox';
import { Checkbox } from 'primereact/checkbox';
import { CalendarMeetingPayload } from 'types/payloads/CalendarMeetingPayload';
import { Button } from 'components/_new/Button';
import { PairCalendarDialog } from 'components/myCalendar/PairCalendarDialog';
import { Dropdown } from 'components/_new/Dropdown';
import { Field } from 'components/_new/Field';
import { useAuth } from 'contexts/authContext';
import { LinkedAccountStatus } from 'types/LinkedAccount';
import { Link } from 'components/_new/Link';
import { Box, BoxSection } from 'components/_new/Box';
import { Divider } from 'components/_new/Divider';
import { LinkedAccountIntegrate } from 'components/myAccount/components/LinkedAccountIntegrate';
import { useLocation } from 'react-router-dom';
import { SessionLinkType } from 'types/SessionMeeting';
import { LinkedAccountList } from 'components/myAccount/components/LinkedAccountList';
import { MessageBox } from 'components/_new/MessageBox';
import { urlValidator } from 'utils/urlValidator';
import { sessionsApi } from 'api/sessions';
import { useCopy } from 'hooks/useCopy';
import { isCalendarAccessDeniedBusinessError } from 'utils/linkedAccountErrorMapping';
import { useSessionsData } from '../Sessions';
import TextField from '../../../_common/forms/TextField';

type EditSessionDetailsDialogProps = DialogBaseExtendProps & {
	previousSession?: Session;
	session: Session;
	nextSession?: Session;
	onFinish?: () => void;
};

const detectMeetingApplicationNameFromLink = (link: string) => {
	switch (true) {
		case Boolean(String(link).match('meet.google')):
			return 'Google Meet';
		case Boolean(String(link).match('teams.microsoft')):
			return 'Microsoft Teams';
		default:
			return 'INNERJ';
	}
};

export const EditSessionDetailsDialog = ({
	previousSession,
	nextSession,
	session,
	onFinish,
	...restProps
}: EditSessionDetailsDialogProps) => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();
	const { copy } = useCopy();
	const { currentUser } = useAuth();
	const { todosRefetch, currentProgramMembership, currentProgram } = usePanel();

	const {
		data: linkedAccounts,
		isFetched: linkedAccountsFetched,
		refetch: linkedAccountsRefetch,
	} = useQuery(['GET_LINKED_ACCOUNTS', { id: Number(currentUser?.id) }], () => linkedAccountApi.getLinkedAccounts(), {
		enabled: restProps.visible,
		initialData: [],
	});

	const [changeCalendarMeetingMode, setChangeCalendarMeetingMode] = useState(true);

	const [sendIcsDialogOpen, setIcsDialogOpen] = useState(false);
	const [saveMode, setSaveMode] = useState(false);

	const [openCalendarDialog, setOpenCalendarDialog] = useState(false);

	const { sessiosRefetch } = useSessionsData();

	const { date, duration, goal, pairId, id, finished, sessionMeeting } = session;

	const {
		control,
		handleSubmit,
		reset: formReset,
		watch,
		setValue,
		getValues,
	} = useForm<
		Pick<SessionPayload, 'date' | 'duration' | 'goal' | 'finished' | 'link' | 'linkType' | 'linkedAccountId'>
	>({
		defaultValues: {
			date: null,
			duration: null,
			goal: '',
			finished: false,
			link: '',
			linkType: null,
			linkedAccountId: null,
		},
	});

	const currentLinkType = watch('linkType');
	const currentLink = watch('link');
	const currentLinkedAccountId = watch('linkedAccountId');
	const currentLinkedAccount = linkedAccounts.find(({ id }) => id === currentLinkedAccountId) || null;
	// const internalMeetingLink = currentLink || sessionMeeting?.link || '';

	useEffect(() => {
		if (currentLinkedAccount?.reauthRequired) {
			setChangeCalendarMeetingMode(true);
		}
	}, [currentLinkedAccount]);

	useEffect(() => {
		if (restProps?.visible && currentLinkType === 'calendar') {
			linkedAccountsRefetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentLinkType, restProps?.visible]);

	const dateFromForm = watch('date');
	const currentDate = dateFromForm ? dayjs(dateFromForm).toDate() : undefined;
	const isInternalMeetingPastDateBlocked = Boolean(
		currentDate && !dayjs(currentDate).isAfter(dayjs().subtract(2, 'hours'))
	);
	useEffect(() => {
		setSaveMode(false);
		if (restProps.visible) {
			reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [restProps.visible]);

	const {
		mutate: updateSession,
		isLoading: isSubmitting,
		reset: updateSessionReset,
	} = useMutation(
		({ date, ...payload }: Omit<SessionPayload, 'id'>) => {
			const data = {
				...payload,
				date: date && String(date).length > 0 ? new Date(date).toISOString() : null,
			};
			return sessionsApi.updateSession(+pairId, currentProgramMembership.id, id, data);
		},
		{
			onSuccess: ({ date, sessionMeeting }) => {
				todosRefetch();
				sessiosRefetch();
				linkedAccountsRefetch();

				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('userPanel.sessions.successfullSessionEditTooltip'),
				});

				handleHide();

				if (date && !dayjs(date).isBefore(dayjs()) && sessionMeeting?.linkType !== SessionLinkType.CALENDAR) {
					setIcsDialogOpen(true);
				}

				setSaveMode(false);
			},
			onError: (error: unknown) => {
				if (isCalendarAccessDeniedBusinessError(error)) {
					toastRef.current?.show({
						severity: 'error',
						life: 5000,
						summary: t('misc.error'),
						detail: t('linkedAccount.session.calendarAccessDeniedOnMeetingEdit'),
					});
				} else {
					const responseData = (error as { response?: { data?: { message?: unknown } } })?.response?.data;
					const detail =
						typeof responseData?.message === 'string' && responseData.message.length > 0
							? responseData.message
							: t('misc.genericError');

					toastRef.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.error'),
						detail,
					});
				}

				setTimeout(() => {
					todosRefetch();
					sessiosRefetch();
					linkedAccountsRefetch();
					setSaveMode(false);
				}, 100);
			},
		}
	);

	const [internalMeetingLink, setInternalLink] = useState<string | null>(null);
	const reset = () => {
		const calendarDate = date ? new Date(date) : undefined;

		setInternalLink(
			sessionMeeting && [SessionLinkType.CALENDAR, SessionLinkType.INTERNAL].includes(sessionMeeting.linkType)
				? sessionMeeting?.link
				: null
		);

		formReset({
			date: calendarDate,
			duration,
			goal,
			finished,
			link: sessionMeeting?.linkType === SessionLinkType.CUSTOM ? sessionMeeting?.link : null,
			linkType: sessionMeeting?.linkType,
			linkedAccountId: sessionMeeting?.linkedAccountId,
		} as Pick<SessionPayload, 'date' | 'goal' | 'finished' | 'link' | 'linkType' | 'linkedAccountId'>);
		if (sessionMeeting?.link) {
			setChangeCalendarMeetingMode(
				!(
					sessionMeeting.link &&
					sessionMeeting.link.length &&
					sessionMeeting.linkType === SessionLinkType.CALENDAR
				)
			);
		} else {
			setChangeCalendarMeetingMode(true);
		}
		updateSessionReset();
	};

	const { mutate: sendCalendarMutate, isLoading: sendCalendarLoading } = useMutation(
		(forMeOnly: CalendarMeetingPayload) =>
			sessionsApi.sendCalendarEmail(session.pairId, currentProgramMembership.id, session.id, forMeOnly),
		{
			onSuccess: () => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('userPanel.sessions.sendICS.success'),
				});
				handleHideCalendarDialog();
			},
		}
	);

	const [forMeOnly, setForMeOnly] = useState(false);

	const handleSubmitFormCalendar = (event: any) => {
		event.preventDefault();
		sendCalendarMutate({ forMeOnly });
	};

	const handleSubmitForm = handleSubmit(({ date, duration, goal, finished, link, linkType, linkedAccountId }) => {
		setSaveMode(true);
		const currentDate = date ? new Date(date).getTime() : null;
		const prevTime = previousSession?.date ? new Date(previousSession?.date).getTime() : null;
		const nextTime = nextSession?.date ? new Date(nextSession?.date).getTime() : null;
		if (prevTime && currentDate && prevTime > currentDate) {
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: t('userPanel.sessionsNew.prvSession'),
				detail: t('userPanel.sessionsNew.prvSessionSub'),
			});
		} else if (currentDate && nextTime && currentDate > nextTime) {
			toastRef?.current?.show({
				severity: 'error',
				life: 3000,
				summary: t('userPanel.sessionsNew.nxtSession'),
				detail: t('userPanel.sessionsNew.nxtSessionSub'),
			});
		} else {
			updateSession({
				date,
				duration,
				goal,
				finished,
				linkType,
				link,
				linkedAccountId,
			});
		}
	});

	const handleHide = () => {
		if (!saveMode) {
			const values = getValues();

			const sessionDateChange =
				(session.date ? new Date(session.date).toISOString() : null) !==
				(values.date ? new Date(values.date || '').toISOString() : null);
			const hasChanges = sessionDateChange || session.goal !== values.goal;

			if (hasChanges) {
				if (window.confirm(t('misc.confirmReject'))) {
					restProps?.onHide?.();
					reset();
				}
			} else {
				reset();
				restProps?.onHide?.();
			}
		} else {
			restProps?.onHide?.();
		}
	};

	// open my account dialog after open page
	const { search } = useLocation();
	useEffect(() => {
		if (restProps.visible && linkedAccountsFetched) {
			const searchParams = new URLSearchParams(search);
			const linkedAccountParam = searchParams.get('la')
				? Number.parseInt(searchParams.get('la') || '', 10)
				: null;
			if (linkedAccountParam !== null && linkedAccounts.length) {
				setTimeout(() => {
					setValue('linkType', SessionLinkType.CALENDAR);
					if (
						linkedAccountParam &&
						linkedAccounts.findIndex(
							({ id, status, info }) =>
								id === linkedAccountParam &&
								status === LinkedAccountStatus.READY &&
								info?.calendarAccess
						) !== -1
					) {
						setValue('linkedAccountId', linkedAccountParam);
						setTimeout(() => {
							searchParams.delete('la');
							const newSearch = searchParams.toString();
							const newUrl = `${window.location.pathname}${newSearch.length ? `?${newSearch}` : ''}`;
							window.history.replaceState({}, '', newUrl);
						}, 100);
					}
				}, 1000);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [restProps.visible, linkedAccountsFetched, search, linkedAccounts]);

	useEffect(() => {
		if (restProps.visible && currentLinkedAccount?.reauthRequired && !changeCalendarMeetingMode) {
			setChangeCalendarMeetingMode(true);
		}
	}, [restProps.visible, currentLinkedAccount, changeCalendarMeetingMode]);

	const saveDisabled = useMemo(() => {
		if (currentLinkType === 'calendar' && currentLinkedAccount && currentLinkedAccount.reauthRequired) {
			return true;
		}
		if ((currentLinkType === 'calendar' && !currentLinkedAccountId) || !String(currentLinkedAccountId).length) {
			return true;
		}
		return false;
	}, [currentLinkType, currentLinkedAccountId, currentLinkedAccount]);

	const sessionTimeOptions = [
		...Array(9)
			.fill(undefined)
			.map((x, y) => ({
				label: `${(y + 1) * 15} ${t('misc.minutes')}`,
				value: (y + 1) * 15,
			})),
		{ label: `240 ${t('misc.minutes')}`, value: 240 },
		{ label: `300 ${t('misc.minutes')}`, value: 300 },
		{ label: `360 ${t('misc.minutes')}`, value: 360 },
	];

	const handleHideCalendarDialog = () => {
		onFinish?.();
		setIcsDialogOpen(false);
	};

	return (
		<>
			<PairCalendarDialog
				selectedSession={session}
				visible={openCalendarDialog}
				onHide={() => setOpenCalendarDialog(false)}
				pairId={pairId}
				defaultCurrentDate={currentDate}
				highlightEventSession={id}
				onAccept={(date) => setValue('date', date as unknown as string)}
			/>
			<DialogAction
				title={t('userPanel.sessions.editSessionAndObjective')}
				actions={[
					{
						key: 'send',
						label: t('userPanel.myCalendar.sendToCalendar'),
						submit: true,
						form: 'send-ics-form',
						loading: sendCalendarLoading,
					},
				]}
				visible={sendIcsDialogOpen}
				onHide={handleHideCalendarDialog}
			>
				<form id="send-ics-form" onSubmit={handleSubmitFormCalendar}>
					<p>{t('userPanel.sessions.sendICS.description')}</p>
					<div className="mb-5">
						<Checkbox
							checked={!forMeOnly}
							className="mt-4 mr-2"
							onChange={(e) => setForMeOnly(!e.target.checked)}
						/>
						{t('userPanel.sessions.sendICS.forMeOnly')}
					</div>
				</form>
			</DialogAction>
			<DialogAction
				title={t('userPanel.sessions.editSessionAndObjective')}
				actions={[
					{
						key: 'save',
						label: t('actions.save'),
						submit: true,
						form: 'edit-session-form',
						loading: isSubmitting,
						disabled: saveDisabled,
					},
				]}
				{...restProps}
				onHide={handleHide}
			>
				<form id="edit-session-form" onSubmit={handleSubmitForm} className="flex flex-column row-gap-3">
					<div className="flex flex-column gap-2 justify-content-start">
						<div>{t('userPanel.sessions.dateLabel')}</div>
						<div className="grid w-full mx-0 mt-0">
							<div className="col-12 md:col-8 p-0 md:pr-2">
								<Calendar control={control} name="date" withHours />
							</div>
							<div className="col-12 md:col-4 p-0 mt-2 md:mt-0">
								<Controller
									name="duration"
									control={control}
									render={({ field }) => (
										<Dropdown
											{...field}
											options={sessionTimeOptions}
											withEmpty
											emptyLabel={t('userPanel.sessions.durationPlaceholder')}
											emptyValue={null}
											minimal
											className="w-full"
										/>
									)}
								/>
							</div>
						</div>

						<div className="flex flex-row justify-content-start">
							<Button
								onClick={() => setOpenCalendarDialog(true)}
								label={t('userPanel.sessions.findDate')}
								variant="primary-text"
								variantSize="sm"
								icon="fa-solid fa-arrow-up-right-from-square"
								iconPosition="right"
							/>
						</div>
					</div>

					<Controller
						name="linkType"
						control={control}
						render={({ field }) => (
							<Field label={t('userPanel.sessions.meetingLinkLabel')}>
								<Dropdown
									{...field}
									withEmpty
									emptyLabel={t('userPanel.sessionsNew.noneOnsite')}
									options={[
										{
											label: t('userPanel.sessionsNew.customLink'),
											value: SessionLinkType.CUSTOM,
										},
										{
											label: t('userPanel.sessionsNew.calendarLink'),
											value: SessionLinkType.CALENDAR,
										},
										...(currentProgram?.mentiwayCallsEnabled
											? [
													{
														label: t('userPanel.sessionsNew.internalLink'),
														value: SessionLinkType.INTERNAL,
													},
											  ]
											: []),
									]}
								/>
							</Field>
						)}
					/>

					{currentLinkType === SessionLinkType.CALENDAR && (
						<Box variant="white-bordered">
							<BoxSection>
								{sessionMeeting?.link &&
									sessionMeeting.link.length &&
									sessionMeeting.linkType === SessionLinkType.CALENDAR && (
										<>
											<div className="flex flex-row gap-2">
												<div className="flex-1 flex flex-column">
													<div>
														{t('userPanel.sessionsNew.meetInApp', {
															app: detectMeetingApplicationNameFromLink(
																sessionMeeting.link
															),
														})}
													</div>
													<Link
														to={sessionMeeting.link}
														variant="standard"
														style={{ lineBreak: 'anywhere' }}
														target="_blank"
													>
														{sessionMeeting.link}
													</Link>
												</div>
												<Button
													onClick={() => {
														setChangeCalendarMeetingMode((prev) => !prev);
													}}
													label={t('actions.change')}
													variant="primary-text"
												/>
											</div>
											{changeCalendarMeetingMode && <Divider />}
										</>
									)}

								{changeCalendarMeetingMode && !currentProgram.calendarApiIntegration && (
									<MessageBox
										message={t('linkedAccount.session.linkedAccountDsabledByProgramSetting')}
									/>
								)}

								{changeCalendarMeetingMode && currentProgram.calendarApiIntegration && (
									<>
										{!linkedAccounts.length && (
											<div className="text-muted">
												{t('linkedAccount.session.noLinkedAccountsInfo')}
											</div>
										)}

										<Field label={t('linkedAccount.session.selectAccount')}>
											<Controller
												name="linkedAccountId"
												control={control}
												render={({ field: { value, onChange } }) => (
													<LinkedAccountList
														enabled={restProps.visible}
														initialData={linkedAccounts}
														integrateParams={{
															sessionId: session.id,
															membershipId: currentProgramMembership?.id,
														}}
														onLabelClick={({ id, status, info }) => {
															const disabled =
																status !== LinkedAccountStatus.READY ||
																!info?.calendarAccess;

															if (!disabled) {
																onChange(id);
															}
														}}
														renderBeforeItem={({ id, status, info, reauthRequired }) => (
															<Checkbox
																checked={value === id}
																onChange={() => onChange(id)}
																disabled={
																	reauthRequired ||
																	status !== LinkedAccountStatus.READY ||
																	!info?.calendarAccess
																}
															/>
														)}
													/>
												)}
											/>
										</Field>

										{!linkedAccounts.filter(({ status }) => status === LinkedAccountStatus.READY)
											.length && (
											<LinkedAccountIntegrate
												sessionId={session.id}
												membershipId={currentProgramMembership.id}
											/>
										)}
									</>
								)}
							</BoxSection>
						</Box>
					)}

					{currentLinkType === SessionLinkType.CUSTOM && (
						<TextField
							control={control}
							rules={{
								required: {
									value: currentLinkType === SessionLinkType.CUSTOM,
									message: t('misc.forms.fieldIsRequired'),
								},
								validate: {
									type: (value: any): boolean | string => {
										if (!value) return true;

										if (/^[\w-]+(\.[\w-]+)+/.test(value) && !/^https?:\/\//i.test(value)) {
											setValue('link', `https://${value}`);
										}

										if (!urlValidator(String(value))) {
											return t('userPanel.sessions.invalidLinkFormat') as string;
										}

										return true;
									},
								},
							}}
							name="link"
							label={t('userPanel.sessionsNew.customLinkLabel')}
						/>
					)}

					{currentLinkType === SessionLinkType.INTERNAL && (
						<>
							{isInternalMeetingPastDateBlocked ? (
								<div className="px-3 text-danger">
									{t('userPanel.sessionsNew.internalMeetingLinkPastDateInfo')}
								</div>
							) : internalMeetingLink ? (
								<div className="flex flex-row align-items-center gap-2 px-2">
									<div className="flex-1 overflow-hidden">
										<Link
											to={internalMeetingLink}
											variant="standard"
											style={{ lineBreak: 'anywhere' }}
											target="_blank"
										>
											<span className="mr-2">
												<i className="pi pi-external-link" />
											</span>
											{internalMeetingLink}
										</Link>
									</div>
									<Button
										variant="primary-text"
										variantSize="sm"
										icon="copy"
										iconOnly
										label={t('misc.copyToClipboard')}
										onClick={async () => {
											await copy(internalMeetingLink, {
												successMessage: t('userPanel.sessionsNew.internalMeetingLinkCopied'),
												errorMessage: t('userPanel.sessionsNew.internalMeetingLinkCopyError'),
											});
										}}
									/>
								</div>
							) : (
								<div className="px-3 text-muted">
									{t('userPanel.sessionsNew.internalMeetingLinkPendingInfo')}
								</div>
							)}
						</>
					)}

					<TextArea control={control} name="goal" label={t('userPanel.sessions.goalLabel')} />
					<ValidateCheckbox
						control={control}
						name="finished"
						text={t('userPanel.sessions.markSessionAsFinished')}
					/>
				</form>
			</DialogAction>
		</>
	);
};
